import {
  BaseQueryFn as RtkBaseQueryFn,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { isEqual, joinValues, stringifyQuery } from 'src/utils';
import { API_DEFAULT_BASE_URL, API_ERROR_EVENT } from './constants';
import { FetchArgs, UseQueryResult, UseQueryStateResult } from './types';

type BaseQueryFn<T = unknown> = RtkBaseQueryFn<
  FetchArgs,
  T,
  FetchBaseQueryError
>;

type BaseQueryParams = {
  baseUrl?: string;
  path?: string;
};

const getApiReqParams = ({ url: reqUrl, queryParams, ...rest }: FetchArgs) => {
  const queryString = queryParams ? stringifyQuery(queryParams) : '';
  const url = (reqUrl ?? '') + queryString;
  return { url, ...rest };
};

export const baseQuery = (params?: BaseQueryParams): BaseQueryFn => {
  const { baseUrl = API_DEFAULT_BASE_URL, path } = params || {};
  const query = fetchBaseQuery({
    baseUrl: joinValues([baseUrl, path]),
  }) as BaseQueryFn;

  return async (args, api, extraOptions) => {
    const accessToken = localStorage.getItem('authToken') ?? '';
    const headers = {
      Authorization: `Bearer ${accessToken || ''}`,
      ...args.headers,
    };
    const result = await query(
      getApiReqParams({ ...args, headers }),
      api,
      extraOptions,
    );
    if (result.error) {
      document.dispatchEvent(
        new CustomEvent(API_ERROR_EVENT, { detail: result.error }),
      );
    }

    return result;
  };
};

export const isLoadingWithNewParams = <R, P>(
  { originalArgs, isFetching, isLoading }: Partial<UseQueryResult<R>>,
  params: P,
) => (isEqual(originalArgs, params) ? isLoading : isFetching);

export const selectFromQueryResult =
  <R, S, P>(selector: (data?: R, params?: P) => S, params?: P) =>
  ({ data, ...rest }: UseQueryStateResult<R>) =>
    ({ data: selector(data, params), ...rest }) as UseQueryStateResult<S>;
